import React, { useEffect, useState } from "react";
import axios from "axios";
import { Lock, Trash2, CheckCircle2, RefreshCw, Mail, Phone, Video as VideoIcon, Save, Play, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIDEO_KEYS = [
  { key: "hero-video", label: "Vídeo principal (Hero)", section: "Topo da página" },
  { key: "about-video", label: "Vídeo da seção Sobre", section: "Sobre nós" },
  { key: "testimonial-v1", label: "Depoimento 1 — Felipe Da Silva", section: "Depoimentos" },
  { key: "testimonial-v2", label: "Depoimento 2 — Erly Penha de Almeida", section: "Depoimentos" },
  { key: "testimonial-v3", label: "Depoimento 3 — Robson Eloi", section: "Depoimentos" },
  { key: "testimonial-v4", label: "Depoimento 4 — Marina S.", section: "Depoimentos" },
  { key: "testimonial-v5", label: "Depoimento 5 — Carla R.", section: "Depoimentos" },
  { key: "testimonial-v6", label: "Depoimento 6 — Paulo F.", section: "Depoimentos" },
];

export default function Admin() {
  const [pwd, setPwd] = useState(localStorage.getItem("vs_admin_pwd") || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem("vs_admin_pwd"));
  const [tab, setTab] = useState("messages");
  const [items, setItems] = useState([]);
  const [videos, setVideos] = useState({});
  const [loading, setLoading] = useState(false);

  const loadMessages = async (p = pwd) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/messages`, { headers: { "X-Admin-Password": p } });
      setItems(res.data || []);
      setAuthed(true);
      localStorage.setItem("vs_admin_pwd", p);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Senha incorreta");
        setAuthed(false);
        localStorage.removeItem("vs_admin_pwd");
      } else {
        toast.error("Erro ao carregar mensagens");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos`);
      setVideos(res.data || {});
    } catch {
      toast.error("Erro ao carregar vídeos");
    }
  };

  useEffect(() => {
    if (authed) {
      loadMessages();
      loadVideos();
    }
    // eslint-disable-next-line
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!pwd) return;
    loadMessages(pwd).then(() => loadVideos());
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/admin/messages/${id}/read`, {}, { headers: { "X-Admin-Password": pwd } });
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch { toast.error("Falhou"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta mensagem?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: { "X-Admin-Password": pwd } });
      setItems((prev) => prev.filter((m) => m.id !== id));
      toast.success("Excluída");
    } catch { toast.error("Falhou"); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <form onSubmit={submit} className="w-full max-w-sm bg-white p-8 rounded-2xl border border-stone-100 shadow-sm" data-testid="admin-login-form">
          <div className="flex items-center gap-2 text-orange-600 mb-2"><Lock className="w-4 h-4" /><span className="text-xs uppercase font-bold tracking-wider">Painel Admin</span></div>
          <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Clínica Vida Saudável</h1>
          <p className="text-sm text-stone-500 mt-1">Insira a senha para acessar.</p>
          <input
            type="password"
            placeholder="Senha"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            data-testid="admin-pwd-input"
            className="mt-5 w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-orange-600 focus:outline-none"
          />
          <button type="submit" data-testid="admin-login-btn" className="mt-3 w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-xl font-semibold">Entrar</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-orange-600 font-bold">Painel Admin</div>
            <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Clínica Vida Saudável</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { loadMessages(); loadVideos(); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-stone-200 text-sm hover:border-orange-600">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
            </button>
            <button onClick={() => { localStorage.removeItem("vs_admin_pwd"); setAuthed(false); setPwd(""); }} className="px-4 py-2 rounded-lg border border-stone-200 text-sm hover:border-red-500 hover:text-red-600">Sair</button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-6 flex gap-1 border-t border-stone-100">
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} icon={<MessageSquare className="w-4 h-4" />}>
            Mensagens {items.filter(m => !m.read).length > 0 && <span className="ml-1.5 text-[10px] bg-orange-600 text-white px-1.5 py-0.5 rounded-full">{items.filter(m => !m.read).length}</span>}
          </TabBtn>
          <TabBtn active={tab === "videos"} onClick={() => setTab("videos")} icon={<VideoIcon className="w-4 h-4" />}>
            Vídeos do site
          </TabBtn>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {tab === "messages" && (
          <div className="space-y-3">
            {items.length === 0 && !loading && <div className="text-stone-500 text-center py-20">Nenhuma mensagem ainda.</div>}
            {items.map((m) => (
              <article key={m.id} data-testid={`msg-${m.id}`} className={`bg-white rounded-2xl border ${m.read ? "border-stone-100" : "border-orange-200"} p-5`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-stone-900">{m.name}</h3>
                      {!m.read && <span className="text-[10px] uppercase tracking-wider bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">Nova</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-stone-500">
                      <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</span>
                      {m.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone}</span>}
                      <span>{new Date(m.created_at).toLocaleString("pt-BR")}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!m.read && (
                      <button onClick={() => markRead(m.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Lida
                      </button>
                    )}
                    <button onClick={() => remove(m.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100">
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-stone-700 leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </article>
            ))}
          </div>
        )}

        {tab === "videos" && (
          <VideosManager videos={videos} pwd={pwd} onChange={loadVideos} />
        )}
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, icon, children }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${active ? "border-orange-600 text-orange-700" : "border-transparent text-stone-600 hover:text-stone-900"}`}>
      {icon} {children}
    </button>
  );
}

function VideosManager({ videos, pwd, onChange }) {
  return (
    <div className="space-y-4">
      <div className="bg-orange-50/60 border border-orange-100 rounded-2xl p-5 text-sm text-stone-700">
        <strong>Como atualizar:</strong> cole a URL completa do YouTube (vídeo normal, Short ou ID) e clique em <em>Salvar</em>. O vídeo aparece para todos os visitantes do site automaticamente.
      </div>
      {VIDEO_KEYS.map((v) => (
        <VideoRow key={v.key} item={v} value={videos[v.key] || ""} pwd={pwd} onChange={onChange} />
      ))}
    </div>
  );
}

function VideoRow({ item, value, pwd, onChange }) {
  const [input, setInput] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => setInput(value), [value]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/videos/${item.key}`,
        { youtube_id: input },
        { headers: { "X-Admin-Password": pwd } }
      );
      toast.success(`Salvo: ${res.data.youtube_id || "vazio"}`);
      onChange && onChange();
    } catch {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setInput("");
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/videos/${item.key}`, { headers: { "X-Admin-Password": pwd } });
      toast.success("Removido");
      onChange && onChange();
    } catch {
      toast.error("Falha ao remover");
    } finally {
      setSaving(false);
    }
  };

  const thumb = value ? `https://img.youtube.com/vi/${value}/mqdefault.jpg` : "";

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-5" data-testid={`video-row-${item.key}`}>
      <div className="flex flex-col md:flex-row gap-5">
        <div className="w-full md:w-44 aspect-video rounded-xl overflow-hidden bg-stone-100 flex items-center justify-center shrink-0">
          {thumb ? (
            <img src={thumb} alt={item.label} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="text-stone-400 text-xs flex flex-col items-center gap-1.5"><Play className="w-6 h-6" /> Sem vídeo</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">{item.section}</div>
          <h3 className="font-semibold text-stone-900 mt-1">{item.label}</h3>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="https://youtu.be/... ou https://youtube.com/shorts/..."
            className="mt-3 w-full px-4 py-2.5 rounded-xl border border-stone-200 focus:border-orange-600 focus:outline-none text-sm"
            data-testid={`video-input-${item.key}`}
          />
          <div className="mt-2 text-xs text-stone-500">Valor atual: <code className="bg-stone-100 px-1.5 py-0.5 rounded">{value || "(vazio)"}</code></div>
          <div className="mt-3 flex gap-2">
            <button onClick={save} disabled={saving} data-testid={`video-save-${item.key}`} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:bg-stone-300 text-white text-sm font-semibold px-4 py-2 rounded-lg">
              <Save className="w-4 h-4" /> Salvar
            </button>
            {value && (
              <button onClick={clear} disabled={saving} className="inline-flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-semibold px-4 py-2 rounded-lg">
                <Trash2 className="w-4 h-4" /> Limpar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
